import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import FeeScheduleTemplateFilterSchema from "work/fee-schedule-templates/_api/queries/contracts/filtering/_schema/fee-schedule-template-filter-schema";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import FeeScheduleTemplateOrderSchema from "work/fee-schedule-templates/_api/queries/contracts/ordering/_schema/fee-schedule-template-order-schema";
import {
  FeeScheduleTemplateOrderField,
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import z from "zod";

export type FeeScheduleTemplateQueryJSON = z.infer<typeof FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ>;

export type FeeScheduleTemplateQueryParams = z.infer<typeof FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryZ>;

export default class FeeScheduleTemplateQuerySchema {
  public static readonly FeeScheduleTemplateQueryJSONZ = z.object({
    filter: FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterJSONZ,
    order: FeeScheduleTemplateOrderSchema.FeeScheduleTemplateOrderZ,
    pagination: PaginationSchema.PaginationZ,
  });

  public static readonly FeeScheduleTemplateQueryZ = z.object({
    filter: FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterZ,
    order: FeeScheduleTemplateOrderSchema.FeeScheduleTemplateOrderZ,
    pagination: PaginationSchema.PaginationZ,
  });

  public static validateJSON(values: unknown): FeeScheduleTemplateQueryJSON {
    return parseOrThrow(FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is FeeScheduleTemplateQueryJSON {
    try {
      FeeScheduleTemplateQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleTemplateQueryJSON): FeeScheduleTemplateQuery {
    const parsed = FeeScheduleTemplateQuerySchema.validateJSON(json);

    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: Guid.fromJSON(parsed.filter.entityId)!,
      query: parsed.filter.query ?? undefined,
      creatorUserId: Guid.fromJSON(parsed.filter.creatorUserId) ?? undefined,
      name: parsed.filter.name ?? undefined,
      minCreatedDate: AHDate.fromJSON(parsed.filter.minCreatedDate) ?? undefined,
      maxCreatedDate: AHDate.fromJSON(parsed.filter.maxCreatedDate) ?? undefined,
      tags: parsed.filter.tags ?? undefined,
      isArchived: AHBoolean.fromJSON(parsed.filter.isArchived ?? undefined) ?? undefined,
      isActive: AHBoolean.fromJSON(parsed.filter.isActive ?? undefined) ?? undefined,
    });

    const orderParameters = parsed.order
      ? new FeeScheduleTemplateOrderParameters({
          orderBy: parsed.order.orderBy!,
          orderDir: parsed.order.orderDir!,
        })
      : new FeeScheduleTemplateOrderParameters({
          orderBy: FeeScheduleTemplateOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        });

    const paginationParameters = PaginationParameters.create({
      pageIndex: parsed.pagination.pageIndex,
      pageSize: parsed.pagination.pageSize,
    });

    return new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }

  public static serialize(query: FeeScheduleTemplateQuery): FeeScheduleTemplateQueryJSON {
    const { filterParameters, orderParameters, paginationParameters } = query;

    const json: FeeScheduleTemplateQueryJSON = {
      filter: {
        entityId: filterParameters.entityId.toJSON(),
        query: filterParameters.query ?? undefined,
        creatorUserId: filterParameters.creatorUserId?.toJSON(),
        name: filterParameters.name ?? undefined,
        minCreatedDate: filterParameters.minCreatedDate?.toJSON(),
        maxCreatedDate: filterParameters.maxCreatedDate?.toJSON(),
        tags: filterParameters.tags ?? undefined,
        isArchived: filterParameters.isArchived?.toJSON(),
        isActive: filterParameters.isActive?.toJSON(),
      },
      order: orderParameters
        ? {
            orderBy: orderParameters.orderBy,
            orderDir: orderParameters.orderDir,
          }
        : undefined,
      pagination: {
        pageIndex: paginationParameters.pageIndex,
        pageSize: paginationParameters.pageSize,
      },
    };

    return parseOrThrow(FeeScheduleTemplateQuerySchema.FeeScheduleTemplateQueryJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "serialize",
      payloadSample: json,
    });
  }
}
